import { useLingui } from '@lingui/react/macro'
import { generateEntrySlug } from 'features/rolodex'
import { TimeSeriesScatterplot } from 'features/timeSeries'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

const ChartContainer = styled.div`
  background: ${({ theme }) => theme.forecast.cardBackground};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.85rem;
  text-align: center;
  padding: 1rem;
`

const DEFAULT_WINDOW_DAYS = 30

interface ClaimProcessDataPanelProps {
  siteId: string
  /** The primary `datapoints` entry id linked from the claim's tags. */
  datapointId?: string
  /** Related/redundant sensor datapoint ids the user added to the chart. */
  extraSensorIds?: string[]
  /** Per-sensor line colors (primary in green, related translucent). */
  sensorColors?: Record<string, string>
}

const ClaimProcessDataPanelInner: FC<ClaimProcessDataPanelProps> = ({
  siteId,
  datapointId,
  extraSensorIds,
  sensorColors,
}) => {
  const { t } = useLingui()
  const slug = useMemo(() => generateEntrySlug(RolodexEntryType.CHART), [])
  const timeRange = useMemo(() => {
    const to = new Date()
    const from = new Date(to.getTime() - DEFAULT_WINDOW_DAYS * 86400_000)
    return { from: from.toISOString(), to: to.toISOString() }
  }, [])

  // Primary first, then any related sensors the user opted to overlay. The
  // override path replaces the whole selection on every set change, so toggling
  // a related sensor on/off from the details panel adds/removes it live.
  const sensorIds = useMemo(() => {
    const ids = datapointId ? [datapointId] : []
    for (const id of extraSensorIds ?? []) {
      if (id && !ids.includes(id)) ids.push(id)
    }
    return ids
  }, [datapointId, extraSensorIds])

  if (!datapointId) {
    // Shared between the Datastreams (dossier) and Equipment/Instruments
    // (subject) panes — keep the copy surface-neutral.
    return (
      <Empty>
        {t`No datastream on the chart — add one from the details panel.`}
      </Empty>
    )
  }

  return (
    <TimeSeriesScatterplot
      chartID={slug}
      siteID={siteId}
      transient
      showToolbar
      showChartSettings={false}
      disableSaving
      overrideSensorIds={sensorIds}
      initialSensorColors={sensorColors}
      deemphasizedSensorIds={extraSensorIds}
      initialTimeRange={timeRange}
      chartContainer={ChartContainer}
      hideFloatingActions
    />
  )
}

/**
 * Remount per distinct primary datapoint so chart state resets cleanly when
 * switching claims. Related-sensor changes within a claim are handled live via
 * `overrideSensorIds` and do not remount.
 */
export const EvidenceProcessDataPanel: FC<ClaimProcessDataPanelProps> = (
  props
) => (
  <ClaimProcessDataPanelInner
    key={`${props.siteId}|${props.datapointId ?? 'none'}`}
    {...props}
  />
)
